import { useInfiniteQuery, UseInfiniteQueryOptions } from 'react-query';
import { associationsService } from 'api/services/associations';
import { ErrorResponse } from 'api/types/errors';
import { FeedDto } from 'api/dtos/associations/feed';
import { AssociationFeedQueryKey } from 'api/types/associations/feed';

export const associationFeedKeys = {
  all: ['associationFeed'] as const,
  lists: () => [...associationFeedKeys.all, 'list'] as const,
  list: (params: AssociationFeedQueryKey) =>
    [...associationFeedKeys.lists(), params] as const,
  infinite: (params: AssociationFeedQueryKey) =>
    [...associationFeedKeys.list(params), 'infinite'] as const
};

export const useAssociationFeedInfinite = (
  params: {
    associationId: string;
    searchTerm?: string;
    limit?: number;
    enabled?: boolean;
    useUnauthenticatedEndpoint?: boolean;
  },
  options?: Omit<
    UseInfiniteQueryOptions<FeedDto, ErrorResponse>,
    'queryKey' | 'queryFn' | 'getNextPageParam'
  >
) => {
  const {
    enabled = true,
    useUnauthenticatedEndpoint = false,
    ...queryParams
  } = params;

  const queryKey = associationFeedKeys.infinite({
    associationId: params.associationId,
    searchTerm: params.searchTerm,
    limit: params.limit
  });

  return useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => {
      return associationsService.getAssociationFeed({
        cursor: pageParam,
        limit: queryParams.limit,
        associationId: queryParams.associationId,
        searchTerm: queryParams.searchTerm,
        useUnauthenticatedEndpoint
      });
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
    enabled: enabled && !!params.associationId,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    ...options
  });
};
